import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GridColDef } from '@mui/x-data-grid-pro';
import { TextField } from '@mui/material';
import useUpdateCostCenterSortorder from './useUpdateCostCenterSortorder';
import { CostCenterSortorderValues } from 'RestAPIs/cost-centers/sortorder';
import { showErrorSnackbar } from 'components/shared/Snackbar';

export const useGetColumns = (rows, setRows, costCenterId): GridColDef[] => {
  const { t } = useTranslation();
  const { mutate: updateSortOrder } = useUpdateCostCenterSortorder();

  const availableWeekHoursValueGetter = ({ row }) =>
    row.percentage && row.weekHoursMin
      ? row.weekHoursMin * (row.percentage / 100)
      : undefined;

  const percentageValueGetter = ({ row }) => {
    const percentage = row.percentage;
    return percentage > 0
      ? `${Number.isInteger(percentage) ? percentage : percentage.toFixed(2)}%`
      : '';
  };

  // Function to render the sortOrder as a text field
  const renderEditableSortOrderCell = (params) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSortOrder = Number(event.target.value);

      // Check if the current sortorder is missing and the user is trying to input "0"
      if (params.row.sortorder == null && newSortOrder === 0) {
        showErrorSnackbar(
          t('formationAndSortorder.sortOrderCannotBeZeroAtFirst')
        );
        return;
      }

      if (!isNaN(newSortOrder)) {
        // Update rows locally
        const updatedRows: CostCenterSortorderValues[] = rows.map(
          (row: CostCenterSortorderValues) =>
            row.benefit_id === params.row.benefit_id
              ? { ...row, sortorder: newSortOrder }
              : row
        );

        const sortedRows: CostCenterSortorderValues[] = updatedRows.sort(
          (a: CostCenterSortorderValues, b: CostCenterSortorderValues) =>
            a.sortorder - b.sortorder
        );
        setRows(sortedRows);

        // Send the updated sortorder, waId, and costCenterId to the server
        updateSortOrder({
          sortorder: newSortOrder,
          waId: params.row.waId,
          costCenterId
        });
      }
    };

    return (
      <TextField
        value={params.value ?? ''}
        onChange={handleChange}
        type="number"
        variant="outlined"
        size="small"
        inputProps={{
          style: { textAlign: 'center' }
        }}
      />
    );
  };

  return useMemo(
    () =>
      [
        {
          field: 'sortorder',
          type: 'number',
          headerName: t('formationAndSortorder.sortOrder'),
          flex: 1,
          headerAlign: 'left',
          align: 'left',
          maxWidth: 120,
          editable: true,
          renderCell: renderEditableSortOrderCell
        },
        {
          field: 'waNr',
          type: 'number',
          headerName: t('wa'),
          flex: 1,
          headerAlign: 'left',
          align: 'left',
          maxWidth: 50
        },
        {
          field: 'empName',
          type: 'string',
          headerName: t('employee'),
          flex: 1,
          minWidth: 250
        },
        {
          field: 'functionDescription',
          type: 'string',
          headerName: t('function'),
          flex: 1,
          minWidth: 200
        },
        {
          field: 'empBrName',
          type: 'string',
          headerName: t('source'),
          flex: 1,
          maxWidth: 175
        },
        {
          field: 'contractTypeName',
          type: 'string',
          headerName: t('contract'),
          flex: 1,
          maxWidth: 75,
          headerAlign: 'center',
          align: 'center'
        },
        {
          field: 'weekHoursMin',
          type: 'number',
          headerName: t('formationAndSortorder.weekHoursMin'),
          flex: 1,
          maxWidth: 100,
          headerAlign: 'center',
          align: 'center'
        },
        {
          field: 'percentage',
          type: 'number',
          headerName: t('formationAndSortorder.planUnitPartitionPercentage'),
          valueGetter: percentageValueGetter,
          flex: 1,
          maxWidth: 160,
          headerAlign: 'center',
          align: 'center'
        },
        {
          field: 'weekHoursAvailable',
          type: 'number',
          headerName: t('formationAndSortorder.weekHoursAvailable'),
          valueGetter: availableWeekHoursValueGetter,
          flex: 1,
          maxWidth: 130,
          headerAlign: 'center',
          align: 'center'
        }
      ] as GridColDef[],
    [t, rows, setRows, updateSortOrder]
  );
};
